import { cn } from '@/lib/utils';
import type { ServicesProps } from '@/types';

/**
 * Services Section Component
 * Displays the main services offered by NeurosEdge
 */
export function Services({ services, className }: ServicesProps) {
  return (
    <section className={cn("py-20 bg-black", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            Our Services
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive solutions designed to transform your infrastructure and security operations
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Individual Service Card Component
 */
interface ServiceCardProps {
  service: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  };
}

function ServiceCard({ service }: ServiceCardProps) {
  const IconComponent = service.icon;
  
  return (
    <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6 space-y-4 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 group">
      {/* Icon */}
      <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors duration-300">
        <IconComponent className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors duration-300" />
      </div>
      
      {/* Content */}
      <h3 className="text-xl font-bold text-white">
        {service.title}
      </h3>
      <p className="text-gray-300 leading-relaxed">
        {service.description}
      </p>
    </div>
  );
}
